"use client";
import { useTranslations } from "next-intl";
import { Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useRef } from "react";
import { useFollowStatus } from "@/hooks/use-follow-status";
import AuthModal from "@/components/ui/auth-modal";
import { ProfileNotificationModal } from "@/components/profile-page/profile-notification-modal";

interface UnifiedFollowButtonProps {
  currentUserId: string | null;
  profileId: string;
  initialFollowing: boolean;
  variant?: "default" | "compact" | "icon" | "profile-stats";
  context?: "user-card" | "profile-page" | "product-header" | "carousel";
  onFollowChange?: (following: boolean) => void;
  className?: string;
  disabled?: boolean;
  profileUsername?: string;
  profileAvatarUrl?: string | null;
  promptNotificationsOnFollow?: boolean;
  followingLabel?: string;
  /**
   * Opt-in foil accent for the `compact` variant's not-following state (feed
   * discovery contexts — mirrors the mobile app's RowCta tone="foil"). The
   * following state stays muted. Off everywhere else.
   */
  foil?: boolean;
}

export function UnifiedFollowButton({
  currentUserId,
  profileId,
  initialFollowing,
  variant = "default",
  context = "user-card",
  onFollowChange,
  className,
  disabled = false,
  profileUsername,
  profileAvatarUrl,
  promptNotificationsOnFollow = true,
  followingLabel,
  foil = false,
}: UnifiedFollowButtonProps) {
  const tFollow = useTranslations("follow");
  const { following, updateFollowStatus } = useFollowStatus(profileId, initialFollowing);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const requestInFlight = useRef(false);
  const notificationRequestInFlight = useRef(false);

  const isCurrentUser = currentUserId === profileId;

  const handleFollow = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!currentUserId) {
      setShowAuthModal(true);
      return;
    }

    if (isCurrentUser || requestInFlight.current) {
      return;
    }

    const newFollowingState = !following;
    updateFollowStatus(newFollowingState);
    setIsLoading(true);
    requestInFlight.current = true;

    try {
      if (newFollowingState) {
        const response = await fetch('/api/follow', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ followedId: profileId }),
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Error following:', error);
          updateFollowStatus(!newFollowingState);
          return;
        }
      } else {
        const response = await fetch(`/api/follow?followedId=${profileId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Error unfollowing:', error);
          updateFollowStatus(!newFollowingState);
          return;
        }
      }

      onFollowChange?.(newFollowingState);
      if (newFollowingState && promptNotificationsOnFollow) {
        setShowNotificationPrompt(true);
      }
    } catch (error) {
      console.error("Follow error:", error);
      updateFollowStatus(!newFollowingState);
    } finally {
      setIsLoading(false);
      requestInFlight.current = false;
    }
  };

  if (isCurrentUser) {
    return null;
  }

  const notificationProfileUsername = profileUsername || "this user";

  const authModal = (
    <AuthModal
      isOpen={showAuthModal}
      onCloseAction={() => setShowAuthModal(false)}
      message={
        profileUsername
          ? tFollow("pleaseLogInFollow", { username: profileUsername })
          : tFollow("pleaseLogInFollowGeneric")
      }
      title={profileUsername ? tFollow("followTitle", { username: profileUsername }) : tFollow("pleaseLogInFollowGeneric")}
      username={profileUsername}
      userImage={profileAvatarUrl ?? undefined}
    />
  );

  const handleNotificationConfirm = () => {
    if (notificationRequestInFlight.current) {
      return;
    }
    notificationRequestInFlight.current = true;
    (async () => {
      try {
        const response = await fetch('/api/profile-notifications/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ profileId }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          console.error('Error subscribing to notification:', error);
        }
      } catch (error) {
        console.error('Error subscribing to notification:', error);
      } finally {
        notificationRequestInFlight.current = false;
      }
    })();
  };

  const notificationModal =
    currentUserId && promptNotificationsOnFollow ? (
      <ProfileNotificationModal
        open={showNotificationPrompt}
        onClose={() => setShowNotificationPrompt(false)}
        onConfirm={handleNotificationConfirm}
        profile={{
          id: profileId,
          username: notificationProfileUsername,
          avatar_url: profileAvatarUrl || undefined,
        }}
        mode="enable"
      />
    ) : null;

  const modalStack = (
    <>
      {authModal}
      {notificationModal}
    </>
  );

  if (variant === "icon") {
    return (
      <>
        {modalStack}
        <Button
          variant="outline"
          size="icon"
          className={`rounded-full border backdrop-blur-md transition-all active:scale-95 h-8 w-8
            ${following
              ? 'bg-black/5 border-transparent hover:bg-black/10 dark:bg-white/10 dark:border-transparent dark:hover:bg-white/15'
              : 'bg-black text-white border-black/30 hover:bg-black dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90'}
            ${isLoading ? 'opacity-70' : ''}
            ${className}`}
          onClick={handleFollow}
          disabled={disabled}
          aria-label={following ? tFollow("unfollow") : tFollow("follow")}
          title={following ? followingLabel : undefined}
        >
          {following ? (
            <Check className="h-4 w-4 text-black dark:text-black" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </Button>
      </>
    );
  }

  if (variant === "profile-stats") {
    return (
      <>
        {modalStack}
        <Button
          variant={following ? "outline-solid" : "default"}
          size="sm"
          disabled={disabled}
          onClick={handleFollow}
          className={`rounded-full flex items-center gap-1 px-2.5 sm:px-3 py-0 text-[13px] font-medium h-8 transition-all active:scale-95 border
            ${following
              ? 'bg-black/5 text-black border-transparent hover:bg-black/10 dark:bg-white/10 dark:text-white dark:border-transparent dark:hover:bg-white/15'
              : 'bg-black text-white border-black/30 hover:bg-black dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90'}
            ${isLoading ? 'opacity-70' : ''}
            ${className}`}
        >
          {following ? <Check className="h-4 w-4 shrink-0" /> : <Plus className="h-4 w-4 shrink-0" />}
          <span>{following ? tFollow("following") : tFollow("follow")}</span>
        </Button>
      </>
    );
  }

  if (variant === "compact") {
    const useFoil = foil && !following;
    return (
      <>
        {modalStack}
        <Button
          variant={useFoil ? "foil" : "outline"}
          size="sm"
          disabled={disabled}
          className={
            useFoil
              ? `shrink rounded-full px-2.5 py-1 h-7 text-xs font-medium flex items-center gap-1.5 border-0 leading-tight
                 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-foreground/40
                 ${isLoading ? 'opacity-70' : ''}
                 ${className ?? ''}`
              : `shrink rounded-full px-2.5 py-1 h-7 text-xs font-medium flex items-center gap-1.5 border transition-all backdrop-blur-md leading-tight active:scale-95
                 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1
                 ${following
                   ? 'bg-black/5 text-black border-transparent hover:bg-black/10 dark:bg-white/10 dark:text-white dark:border-transparent dark:hover:bg-white/15 focus-visible:ring-black/30 dark:focus-visible:ring-white/40'
                   : 'bg-black text-white border-black/30 hover:bg-black dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90 focus-visible:ring-black/40 dark:focus-visible:ring-white/50'}
                 ${isLoading ? 'opacity-70' : ''}
                 ${className ?? ''}`
          }
          onClick={handleFollow}
        >
          {following ? <Check className="h-3 w-3 shrink-0" /> : <Plus className="h-3 w-3 shrink-0" />}
          <span className="block truncate">{following ? tFollow("following") : tFollow("follow")}</span>
        </Button>
      </>
    );
  }

  return (
    <>
      {modalStack}
      <Button
        variant="outline"
        size="default"
        disabled={disabled}
        className={`shrink rounded-full px-3 sm:px-3.5 py-1.5 sm:py-1.5 h-8 sm:h-[34px] text-[13px] sm:text-[13px] font-medium flex items-center gap-1.5 sm:gap-1.5 border transition-all backdrop-blur-md leading-tight active:scale-95
          max-w-[46vw] xs:max-w-[42vw] sm:max-w-[220px] md:max-w-[260px]
          whitespace-nowrap overflow-hidden text-ellipsis
          focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1
          ${following
            ? 'bg-black/5 text-black border-transparent hover:bg-black/10 dark:bg-white/10 dark:text-white dark:border-transparent dark:hover:bg-white/15 focus-visible:ring-black/30 dark:focus-visible:ring-white/40'
            : 'bg-black text-white border-black/30 hover:bg-black dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90 focus-visible:ring-black/40 dark:focus-visible:ring-white/50'}
          ${isLoading ? 'opacity-70' : ''}
          ${className}`}
        onClick={handleFollow}
      >
        {following ? <Check className="h-3.5 w-3.5 sm:h-3.5 sm:w-3.5 shrink-0" /> : <Plus className="h-3.5 w-3.5 sm:h-3.5 sm:w-3.5 shrink-0" />}
        <span className="block truncate">{following ? tFollow("following") : tFollow("follow")}</span>
      </Button>
    </>
  );
}

export default UnifiedFollowButton;

